import React, {
    useState,
    useEffect,
    useMemo,
    forwardRef,
    useImperativeHandle,
} from "react";
import { MdSearchOff, MdDelete, MdEdit } from "react-icons/md";
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from "react-icons/md";
import Pagination from "../Pagination/Pagination";
import OptionsMenu from "../OptionsMenu/OptionsMenu";
import Badge from "../Badge/Badge";
import "./Table.scss";
import {
    BadgeProps,
    FilterField,
    FormGroup,
    NotificationHandle,
} from "../../../types/types";
import type { ReactNode } from "react";
import { useTableCRUD } from "../../../utils/useTableCRUD";
import Button from "../Button/Button";
import Modal from "../Modal/Modal";
import FormModal from "../FormModal/FormModal";
import { IoMdAdd } from "react-icons/io";
import { Link, useLocation } from "react-router-dom";
import { hasFileInData, mappedDataToFormData } from "../../../utils/utils";
import Notification from "../notification/notification";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import i18n from "../../../i8n";
import InputDropdown_New from "../../inputs/InputDropdown/InputDropdown_New";
import InputTextarea from "../Inputs/InputTextarea/InputTextarea";
import { useZone } from "../../../context/ZoneContext";
import { getApiInstance } from "../../../utils/api";
import { TiDeleteOutline } from "react-icons/ti";
import { MdOutlineEdit } from "react-icons/md";
import { MdOutlineFileDownload } from "react-icons/md";
import { FilterBar } from "../../filtersNew/filter-barNew";
import {
    applyFilterState,
    createFilterState,
} from "../../../lib/reportNew/filter-stateNew";
import { inferObjectFields } from "../../../lib/reportNew/fieldsNew";
import {
    accionPermitida as accionPermitidaPorPerfil,
    esCampoZona,
    ocultarZona as ocultarZonaPorPerfil,
    puedeAnadirEnTabla,
} from "../../../utils/permisos";

type CustomAction<T> = {
    label: string;
    icon?: ReactNode;
    action?: (row: T) => void;
    render?: (row: T) => ReactNode;
    actionType?: "validate" | "history";
    danger?: boolean;
    separator?: boolean;
};

type ActionItem<T> = "Eliminar" | "Editar" | "Exportar" | CustomAction<T>;

type Column<T> = {
    key: keyof T | string[];
    label: string;
    visible: boolean;
    isLink?: boolean;
    sortable?: boolean;
    documentProp?: string;
    align?: "left" | "center" | "right";
    width?: string;
    type?: "text" | "number" | "date" | "status" | "badge" | "document";
    linkPath?: (row: T) => string;
    render?: (row: T) => ReactNode;
    badgeConfig?: {
        [key: string]: { variant: string; icon?: ReactNode; label?: string };
    } & { default?: { variant: string; icon?: ReactNode; label?: string } };
    badgeProps?: (row: T) => { type: string; label: string; Icon?: ReactNode };
    /** Campo de la fila que se usa como tooltip de la celda de estado
     *  (p. ej. la observación de la última validación). */
    titleProp?: string;
    bold?: boolean;
    secondary?: keyof T | string[];
    visibleOnExpand?: boolean;
};

type TableProps<T> = {
    columns: Column<T>[];
    dataTable: T[];
    noResultsMessage?: string;
    rowsPerPage?: number;
    topContent?: ReactNode;
    topLeftContent?: ReactNode;
    showCheckboxes?: boolean;
    searchTerms?: string[];
    deleteApiUrl?: string;
    addApiUrl?: string;
    editApiUrl?: string;
    formElements?: FormGroup[];
    editFormElements?: FormGroup[];
    actions?: ActionItem<T>[] | ((row: T) => ActionItem<T>[]);
    elementName?: string;
    keyProp?: string;
    sortProp?: string;
    states?: BadgeProps[];
    stateProp: string;
    filterFields?: FilterField<T>[];
    loading: boolean;
    selectedRows?: Set<React.Key>;
    onSelectionChange?: (newSelection: Set<React.Key>) => void;
    stateChangeUrl?: string;
    stateChangeOptions?: { value: any; label: string }[];
    /** Tipo de validación (TipoValidacionTable del backend). Si no se pasa, se
     *  deduce de `addApiUrl`, que no siempre existe. Mejor pasarlo explícito. */
    codTipoValidacion?: number;
    expandable?: boolean;
    expandableBreakpoint?: number;
    renderExpandedContent?: (row: T) => ReactNode;
    userCodAcceso?: number;
    userCodPerfil?: number;
    editOnRowClick?: boolean;
    onRowClick?: (row: T) => void;
    modalAddTitle?: string;
    exportApiUrl?: string;
    /** Texto de la acción "Exportar" cuando el informe tiene nombre propio
     *  (p. ej. "Generar manifiesto"). Por defecto, el genérico traducido. */
    exportActionLabel?: string;
    extraPropExportKey?: string;
    extraPropExportText?: string;
    onDataUpdate?: (newData: T[]) => void;
    extraTopButtons?: ReactNode;
    getRowClassName?: (row: T) => string;
    successMessages?: {
        add?: string;
        edit?: string;
        delete?: string;
    };
    hideAddButton?: boolean;
    /** El intermediario (perfil 4) puede añadir, editar y eliminar en esta tabla,
     *  como en la aplicación antigua (residuos y documentos de su obra, gestores,
     *  obras menores). Validar sigue siendo solo de administradores. Las acciones
     *  que se ofrecen las decide la página. */
    intermediarioPuedeEscribir?: boolean;
    excludeFields?: string[];
    hideFilterBar?: boolean;
    filesAsBase64?: boolean;
    payloadMapper?: (payload: { [key: string]: any }) => {
        [key: string]: any;
    };
    /**
     * Transforma la fila antes de volcarla en el formulario de edición, cuando la
     * unidad del formulario no es la del dato (p. ej. el peso se guarda en kg y
     * los residuos no peligrosos se editan en toneladas). El camino de vuelta es
     * `payloadMapper`: los dos tienen que ser inversos.
     */
    rowToFormMapper?: (row: T) => { [key: string]: any };
    /**
     * Validación propia de la página, más allá de los campos requeridos.
     * Se ejecuta antes de enviar. Devuelve el mensaje de error, o null si todo está bien.
     */
    validateForm?: (
        data: { [key: string]: any },
        mode: "add" | "edit"
    ) => string | null;
    showAdvancedFilters?: boolean;
};

const readFileAsBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const result =
                typeof reader.result === "string" ? reader.result : "";
            const commaIndex = result.indexOf(",");
            resolve(
                commaIndex >= 0 ? result.slice(commaIndex + 1) : result
            );
        };
        reader.onerror = () =>
            reject(
                reader.error ?? new Error(i18n.t("ui.table.readFileError") ?? "")
            );
        reader.readAsDataURL(file);
    });

const toBase64Payload = async (data: {
    [key: string]: any;
}): Promise<{ [key: string]: any }> => {
    const out: { [key: string]: any } = {};
    for (const key of Object.keys(data)) {
        const value = data[key];
        if (value instanceof File || value instanceof Blob) {
            out[key] = await readFileAsBase64(value as File);
        } else if (Array.isArray(value)) {
            out[key] = await Promise.all(
                value.map((item) =>
                    item instanceof File
                        ? readFileAsBase64(item)
                        : item
                )
            );
        } else if (
            value &&
            typeof value === "object" &&
            !(value instanceof Date)
        ) {
            out[key] = await toBase64Payload(value);
        } else {
            out[key] = value;
        }
    }
    return out;
};

export type TableHandle = {
    openAddModal: () => void;
    // Filas que el usuario esta viendo: filtradas y ordenadas, todas las paginas.
    // La usan las exportaciones que tienen que respetar los filtros de la tabla.
    getVisibleRows: () => any[];
};

const Table = forwardRef(function TableInner<
    T extends { id: number }
>(
    {
        columns: columnsProp,
        dataTable = [],
        onDataUpdate = () => {},
        noResultsMessage,
        rowsPerPage = 10,
        topContent,
        topLeftContent,
        showCheckboxes = false,
        searchTerms = [],
        formElements,
        editFormElements,
        actions,
        elementName,
        keyProp = "",
        states,
        stateProp,
        addApiUrl,
        editApiUrl,
        deleteApiUrl,
        filterFields: filterFieldsProp = [],
        loading = false,
        sortProp = keyProp,
        selectedRows,
        onSelectionChange,
        stateChangeUrl,
        stateChangeOptions,
        codTipoValidacion,
        expandable = false,
        expandableBreakpoint = 1024,
        renderExpandedContent,
        userCodAcceso,
        userCodPerfil,
        editOnRowClick = false,
        onRowClick,
        modalAddTitle,
        exportApiUrl,
        exportActionLabel,
        extraPropExportKey = "",
        extraPropExportText = "",
        extraTopButtons,
        getRowClassName,
        successMessages,
        hideAddButton = false,
        intermediarioPuedeEscribir = false,
        excludeFields: excludeFieldsProp = [],
        hideFilterBar = false,
        filesAsBase64 = false,
        payloadMapper,
        rowToFormMapper,
        validateForm,
        showAdvancedFilters = true,
    }: TableProps<T>,
    ref: React.ForwardedRef<TableHandle>
) {
    /* Fuera del Administrador global el usuario solo ve su zona (el backend ya
       filtra los datos), así que el filtro y la columna de Zona solo enseñarían
       un valor. Se quitan aquí para todas las tablas (utils/permisos). */
    const ocultarZona = ocultarZonaPorPerfil(userCodPerfil, dataTable);

    const filterFields = useMemo(
        () =>
            ocultarZona
                ? (filterFieldsProp ?? []).filter(
                      (field) => !esCampoZona(field.key)
                  )
                : filterFieldsProp,
        [filterFieldsProp, ocultarZona]
    );
    const columns = useMemo(
        () =>
            ocultarZona
                ? columnsProp.filter((column) => !esCampoZona(column.key))
                : columnsProp,
        [columnsProp, ocultarZona]
    );
    const excludeFields = useMemo(
        () =>
            ocultarZona
                ? [...(excludeFieldsProp ?? []), "codZona", "zona"]
                : excludeFieldsProp,
        [excludeFieldsProp, ocultarZona]
    );

    const {
        data,
        createItem,
        updateItem,
        deleteItem,
        isLoading,
        error,
        updateLocalData,
    } = useTableCRUD<T>(dataTable);

    const { t } = useTranslation();
    const [currentPage, setCurrentPage] = useState(1);
    const [selectAll, setSelectAll] = useState(false);
    const [filteredData, setFilteredData] = useState<T[]>(dataTable);

    useEffect(() => {
        setFilteredData(data ?? []);
    }, [data]);
    const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
    const [rowToDelete, setRowToDelete] = useState<T | null>(null);
    const [isStateModalOpen, setIsStateModalOpen] = useState(false);
    const [stateModalRow, setStateModalRow] = useState<T | null>(null);
    const [newStateValue, setNewStateValue] = useState<any>(null);
    const [observations, setObservations] = useState<string>("");
    const [isChangingState, setIsChangingState] = useState(false);
    const [missingNames, setMissingNames] = useState<string[]>([]);
    const [expandedRows, setExpandedRows] = useState<Set<React.Key>>(new Set());
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
    const [isSmallMobile, setIsSmallMobile] = useState(
        window.innerWidth <= 480
    );
    const { selectedZone } = useZone();

    const [isBulkStateChange, setIsBulkStateChange] = useState(false);

    /* Validar es cosa de administradores: perfil 1 (global) y 2 (zona), y solo con
       acceso de escritura. Es la regla de la aplicación antigua
       (RolesUsuario en wlc_back/Utils/Constantes.cs). */
    const canChangeState =
        !!stateChangeUrl &&
        !!stateChangeOptions &&
        userCodAcceso === 1 &&
        (userCodPerfil === 1 || userCodPerfil === 2);

    const enableRowSelection = showCheckboxes || canChangeState;

    /* El intermediario (perfil 4) consulta pero no modifica: en lugar de
       esconderle el desplegable entero, se le quitan las acciones que escriben.
       Así conserva las de consulta ("Ver validaciones", exportar...), que es lo
       que hacía la aplicación antigua. */
    /* Con intermediarioPuedeEscribir solo se le quita validar (utils/permisos). */
    const accionPermitida = (action: ActionItem<T>) =>
        accionPermitidaPorPerfil(
            userCodPerfil,
            action,
            intermediarioPuedeEscribir
        );

    const puedeAnadir = puedeAnadirEnTabla(
        userCodPerfil,
        intermediarioPuedeEscribir
    );

    /* Algunas pantallas pasan las acciones ya traducidas (t("common.delete")).
       Se devuelven a su nombre interno para que permisos y menú las reconozcan
       en cualquier idioma. */
    const normalizarAccion = (action: ActionItem<T>): ActionItem<T> => {
        if (typeof action !== "string") return action;
        const enIdiomas = (key: string) =>
            ["es", "en", "es-MX"].map((lng) => i18n.t(key, { lng }));
        if (enIdiomas("common.delete").includes(action)) return "Eliminar";
        if (enIdiomas("common.edit").includes(action)) return "Editar";
        if (enIdiomas("common.export").includes(action)) return "Exportar";
        return action;
    };

    const accionesDeFila = (row: T): ActionItem<T>[] => {
        const lista = typeof actions === "function" ? actions(row) : actions;

        if (!lista) return [];

        return lista.map(normalizarAccion).filter(accionPermitida);
    };

    const hayColumnaAcciones =
        !!actions &&
        userCodAcceso === 1 &&
        (typeof actions === "function"
            ? true
            : actions.map(normalizarAccion).some(accionPermitida));

    const [internalSelectedRows, setInternalSelectedRows] = useState<
        Set<React.Key>
    >(new Set());

    const isSelectionControlled =
        selectedRows !== undefined && onSelectionChange !== undefined;

    const effectiveSelectedRows = isSelectionControlled
        ? selectedRows
        : internalSelectedRows;

    const setSelection = (newSelection: Set<React.Key>) => {
        if (isSelectionControlled) {
            onSelectionChange?.(newSelection);
        } else {
            setInternalSelectedRows(newSelection);
        }
    };

    const notificationRef = useRef<NotificationHandle | null>(null);

    const loadingData = loading;

    const location = useLocation();
    const base = location.pathname;

    useEffect(() => {
        setCurrentPage(1);
    }, [filteredData]);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= expandableBreakpoint);
            setIsSmallMobile(window.innerWidth <= 480);
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [expandableBreakpoint]);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalData, setModalData] = useState<{ [key: string]: any }>({});
    const [modalType, setModalType] = useState("add");
    const [dynamicFormElements, setDynamicFormElements] = useState<FormGroup[]>(
        formElements || []
    );
    const [associatedOptions, setAssociatedOptions] = useState<{
        [key: string]: any[];
    }>({});

    const filterBarFields = useMemo(() => {
        const typeMap: Record<string, string> = {
            select: "enum",
            multiselect: "enum",
            cards: "enum",
            date: "date",
            daterange: "dateRange",
            number: "number",
            text: "text",
            boolean: "boolean",
        };
        const mapped = (filterFields ?? []).map((field) => ({
            key: String(field.key),
            label: field.label,
            type: typeMap[String(field.type).toLowerCase()] ?? "text",
            ...(field.objectProp ? { objectProp: field.objectProp } : {}),
            ...(field.rangeStartField
                ? { rangeStartField: field.rangeStartField }
                : {}),
            ...(field.rangeEndField ? { rangeEndField: field.rangeEndField } : {}),
            ...(field.labelMap ? { labelMap: field.labelMap } : {}),
        }));
        const extra = (searchTerms ?? [])
            .filter((key) => !mapped.some((item) => item.key === key))
            .map((key) => ({ key, label: key, type: "text" }));
        // Los rangos de fechas filtran sobre campos reales (fechaInicio/fin);
        // hay que registrarlos como campos "date" para que el motor resuelva
        // sus operadores y no descarte las condiciones generadas.
        const rangeHelpers: Record<string, string>[] = [];
        (filterFields ?? [])
            .filter((field) => String(field.type).toLowerCase() === "daterange")
            .forEach((field) => {
                [
                    { key: field.rangeStartField || field.key, label: `${field.label} (inicio)` },
                    { key: field.rangeEndField || field.key, label: `${field.label} (fin)` },
                ].forEach((helper) => {
                    const key = String(helper.key);
                    if (
                        !mapped.some((item) => item.key === key) &&
                        !rangeHelpers.some((item) => item.key === key)
                    ) {
                        rangeHelpers.push({
                            key,
                            label: String(helper.label),
                            type: "date",
                        });
                    }
                });
            });
        return [...mapped, ...extra, ...rangeHelpers, ...inferObjectFields(filteredData ?? [])];
    }, [filterFields, searchTerms, filteredData]);

    const filterQuickKeys = useMemo(
        () =>
            (filterFields ?? [])
                .filter((field) => field.advanced !== true)
                .map((field) => String(field.key)),
        [filterFields]
    );

    const [filterState, setFilterState] = useState(() => createFilterState([]));

    const quickKeysSignature = filterQuickKeys.join("|");
    useEffect(() => {
        setFilterState(
            createFilterState(quickKeysSignature ? quickKeysSignature.split("|") : [])
        );
    }, [quickKeysSignature]);

    useEffect(() => {
        setDynamicFormElements(
            modalType === "edit" && editFormElements
                ? editFormElements
                : formElements || []
        );
    }, [formElements, editFormElements, modalType]);

    useEffect(() => {
        setCurrentPage(1);
    }, [dataTable]);

    const [sortConfig, setSortConfig] = useState<{
        key: keyof T | null;
        direction: "ascending" | "descending" | null;
    }>({
        key: null,
        direction: null,
    });

    const getNestedValue = (obj: any, path: keyof any | string[]): any => {
        if (Array.isArray(path)) {
            return path.reduce((acc, part) => acc && acc[part], obj);
        }
        return obj[path];
    };

    const normalizeSortString = (value: string): string =>
        value
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .toLowerCase()
            .trim();

    const indexOfLastRow = currentPage * rowsPerPage;
    const indexOfFirstRow = indexOfLastRow - rowsPerPage;

    const filteredRows = useMemo(
        () =>
            applyFilterState(
                filteredData ?? [],
                filterState,
                filterBarFields
            ) as T[],
        [filteredData, filterState, filterBarFields]
    );

    const sortedData = useMemo(() => {
        if (
            !filteredRows ||
            filteredRows == undefined ||
            filteredRows.length == 0
        ) {
            return [];
        }

        let sortableData = [...filteredRows];

        if (sortConfig.key && sortConfig.direction) {
            const isAscending =
                sortConfig.direction === "ascending";

            sortableData.sort((a, b) => {
                const key = sortConfig.key as keyof T;
                const valueA = getNestedValue(a, key);
                const valueB = getNestedValue(b, key);

                if (valueA === null || valueA === undefined) return 1;
                if (valueB === null || valueB === undefined) return -1;

                if (
                    typeof valueA === "string" &&
                    typeof valueB === "string"
                ) {
                    const a = normalizeSortString(valueA);
                    const b = normalizeSortString(valueB);
                    if (a < b) return isAscending ? -1 : 1;
                    if (a > b) return isAscending ? 1 : -1;
                    return 0;
                }

                if (valueA < valueB) {
                    return isAscending ? -1 : 1;
                }
                if (valueA > valueB) {
                    return isAscending ? 1 : -1;
                }
                return 0;
            });
        }

        return sortableData;
    }, [filteredRows, sortConfig]);

    const currentRows = sortedData.slice(indexOfFirstRow, indexOfLastRow);
    const totalPages = Math.ceil(filteredRows.length / rowsPerPage);

    const requestSort = (key: keyof T) => {
        let direction: "ascending" | "descending" | null = "ascending";
        if (sortConfig.key === key) {
            direction =
                sortConfig.direction === "ascending" ? "descending" : null;
        }
        setSortConfig({ key: direction ? key : null, direction });
    };

    const handleColumnSort = (key: keyof T | string[]) => {
        if (Array.isArray(key)) return;
        requestSort(key as keyof T);
    };

    const toggleRowExpansion = (rowId: React.Key) => {
        const newExpanded = new Set(expandedRows);
        if (newExpanded.has(rowId)) {
            newExpanded.delete(rowId);
        } else {
            newExpanded.add(rowId);
        }
        setExpandedRows(newExpanded);
    };

    const handleSelectAll = () => {
        if (selectAll) {
            setSelection(new Set());
        } else {
            const allIds = new Set(
                filteredRows.map((row) => row[keyProp as keyof T] as React.Key)
            );
            setSelection(allIds);
        }
    };

    const handleSelectRow = (rowId: React.Key) => {
        const newSelected = new Set(effectiveSelectedRows);

        if (newSelected.has(rowId)) {
            newSelected.delete(rowId);
        } else {
            newSelected.add(rowId);
        }

        setSelection(newSelected);
    };

    const renderStatusBadge = (status: string) => {
        const statusMap: { [key: string]: string } = {
            approved: "approved",
            "in review": "in-review",
            draft: "draft",
            rejected: "rejected",
        };

        const badgeClass = statusMap[status.toLowerCase()] || "draft";

        return <span className={`status-badge ${badgeClass}`}>{status}</span>;
    };

    /* Valores de TipoValidacionTable (wlc_back/Utils/Constantes.cs):
       1 Obras, 2 ResiduosObra, 6 ObrasDocumentos, 7 ProveedoresDocumentos,
       9 ObrasMenoresDocumentos. */
    const getCodTipoValidacion = () => {
        if (codTipoValidacion != null) return codTipoValidacion;

        const elementIdentificator = addApiUrl?.split("/")[2] || "";

        switch (elementIdentificator) {
            case "ObraDocumento":
                return 6;
            case "ResiduosObra":
                return 2;
            case "GestorDocumentos":
                return 7;
            case "ObraMenorDocumento":
                return 9;
            default:
                return 0;
        }
    };

    const buildStateDto = (row: T) => {
        const rowAny = row as any;
        const id = rowAny[keyProp];

        /* Los valores del selector son cadenas y ValidacionesDTO los espera
           numéricos: System.Text.Json no convierte "3" a byte y devolvería 400. */
        return {
            CodTipoValidacion: getCodTipoValidacion(),
            CodClave: Number(rowAny.CodClave ?? id) || 0,
            Observaciones: String(observations ?? ""),
            CodEstado: Number(newStateValue?.value) || 0,
            CodZona: Number(selectedZone?.value) || 0,
            CodZonaLegal: Number(selectedZone?.value) || 0,
        };
    };

    const openBulkStateModal = () => {
        if (!canChangeState || effectiveSelectedRows.size === 0) return;

        setIsBulkStateChange(true);
        setStateModalRow(null);
        setNewStateValue(null);
        setObservations("");
        setIsStateModalOpen(true);
    };

    const openStateModal = (row: T) => {
        if (canChangeState) {
            setIsBulkStateChange(false);
            setStateModalRow(row);
            const current = (row as any)[stateProp];
            setNewStateValue(
                stateChangeOptions?.find((x) => x.value == current) ?? null
            );
            setObservations("");
            setIsStateModalOpen(true);
        }
    };

    const closeStateModal = () => {
        setIsStateModalOpen(false);
        setStateModalRow(null);
        setNewStateValue(null);
        setObservations("");
        setIsBulkStateChange(false);
    };

    useEffect(() => {
        if (!enableRowSelection) {
            setSelectAll(false);
            return;
        }

        const allFilteredIds = filteredRows.map(
            (row) => row[keyProp as keyof T] as React.Key
        );

        setSelectAll(
            allFilteredIds.length > 0 &&
                allFilteredIds.every((id) => effectiveSelectedRows.has(id))
        );
    }, [filteredRows, effectiveSelectedRows, keyProp, enableRowSelection]);

    const confirmStateChange = async () => {
        if (
            !stateChangeUrl ||
            newStateValue === null ||
            newStateValue === undefined
        ) {
            return;
        }

        try {
            setIsChangingState(true);

            const sourceData = (data ?? []) as T[];

            if (isBulkStateChange) {
                const rowsToUpdate = sourceData.filter((row: any) =>
                    effectiveSelectedRows.has(row[keyProp] as React.Key)
                );

                if (rowsToUpdate.length === 0) return;

                const dtos = rowsToUpdate.map(buildStateDto);

                stateChangeUrl = "/api/Validaciones/SetValidaciones";

                await createItem(stateChangeUrl, dtos, keyProp, sortProp, {
                    mutate: false,
                });

                const selectedIds = new Set(
                    rowsToUpdate.map((r: any) => String(r[keyProp]))
                );

                setFilteredData((prev) =>
                    prev.map((r: any) =>
                        selectedIds.has(String(r[keyProp]))
                            ? { ...r, [stateProp]: Number(newStateValue.value) }
                            : r
                    )
                );

                if (Array.isArray(data)) {
                    const updated = data.map((r: any) =>
                        selectedIds.has(String(r[keyProp]))
                            ? { ...r, [stateProp]: Number(newStateValue.value) }
                            : r
                    );
                    updateLocalData(updated);
                    onDataUpdate?.(updated);
                }

                setSelection(new Set());
                notificationRef.current?.addNotification(
                    (t("ui.table.statesUpdated") ?? ""),
                    "success"
                );
            } else {
                if (!stateModalRow) return;

                const rowAny = stateModalRow as any;
                const id = rowAny[keyProp];
                const dto = buildStateDto(stateModalRow);

                await createItem(stateChangeUrl, dto, keyProp, sortProp, {
                    mutate: false,
                });

                setFilteredData((prev) =>
                    prev.map((r: any) =>
                        String(r?.[keyProp]) === String(id)
                            ? { ...r, [stateProp]: Number(newStateValue.value) }
                            : r
                    )
                );

                if (Array.isArray(data)) {
                    const updated = data.map((r: any) =>
                        String(r?.[keyProp]) === String(id)
                            ? { ...r, [stateProp]: Number(newStateValue.value) }
                            : r
                    );
                    updateLocalData(updated);
                    onDataUpdate?.(updated);
                }

                notificationRef.current?.addNotification(
                    (t("ui.table.stateUpdated") ?? ""),
                    "success"
                );
            }

            closeStateModal();
        } catch (e) {
            console.error("Error al cambiar estado:", e);
            notificationRef.current?.addNotification(
                "No se pudo actualizar el estado",
                "error"
            );
        } finally {
            setIsChangingState(false);
        }
    };

    const openModal = () => {
        setIsModalOpen(true);
        setModalType("add");
        const defaults: { [key: string]: any } =
            formElements === undefined
                ? {}
                : formElements
                      .flatMap((group) => group.elements)
                      .filter((el) => el.defaultValue !== undefined)
                      .reduce(
                          (acc, el) => {
                              acc[el.name] = el.defaultValue;
                              return acc;
                          },
                          {} as { [key: string]: any }
                      );
        // Sin esto el alta hereda los desplegables dependientes de la última edición
        setAssociatedOptions(calculateAssociatedOptions(defaults));
        setModalData(defaults);
    };

    useImperativeHandle(ref, () => ({
        openAddModal: openModal,
        getVisibleRows: () => sortedData,
    }));

    const closeModal = () => {
        setIsModalOpen(false);
        setMissingNames([]);
        setModalData({});
    };

    const renderCellValue = (row: T, column: Column<T>): ReactNode => {
        var value = getNestedValue(row, column.key);

        if (column.render) {
            return column.render(row);
        }

        if (column.isLink && value) {
            const href = `${base}/${(row as Record<string, any>)[keyProp]}`;
            return (
                <Link className="link-style" to={href}>
                    {String(value)}
                </Link>
            );
        }

        if (column.type === "date" && value) {
            return new Date(String(value)).toLocaleDateString();
        }

        if (column.type === "status" && value) {
            return renderStatusBadge(String(value));
        }

        if (
            column.type === "document" &&
            value &&
            column.documentProp &&
            row[column.documentProp as keyof T]
        ) {
            const base64 = row[column.documentProp as keyof T] as string;
            const mime = "application/pdf";
            const href = `data:${mime};base64,${base64}`;
            const fileName = String(value);
            return (
                <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    download={fileName}
                >
                    {fileName || "Ver documento"}
                </a>
            );
        }

        if (stateProp == column.key && states) {
            if (states && states[(row as Record<string, any>)[stateProp]]) {
                const stateObj =
                    states[(row as Record<string, any>)[stateProp]];

                /* La aplicación antigua ponía la observación de la última
                   validación como tooltip de esta celda. */
                const stateNote = column.titleProp
                    ? (row as Record<string, any>)[column.titleProp]
                    : undefined;

                if (
                    stateChangeOptions === undefined ||
                    stateChangeUrl === undefined ||
                    !canChangeState
                ) {
                    return (
                        <span title={stateNote || undefined}>
                            <Badge variant={stateObj.type} Icon={stateObj.Icon}>
                                {stateObj.label}
                            </Badge>
                        </span>
                    );
                }
                return (
                    <span
                        onClick={(e) => {
                            e.stopPropagation();
                            openStateModal(row);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                openStateModal(row);
                            }
                        }}
                        role="button"
                        tabIndex={0}
                        style={{
                            cursor: "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                        }}
                        aria-label={(t("ui.table.changeState") ?? "")}
                        title={
                            stateNote ||
                            (isBulkStateChange
                                ? `Cambiar estado (${effectiveSelectedRows.size} seleccionados)`
                                : "Cambiar estado")
                        }
                    >
                        <Badge variant={stateObj.type} Icon={stateObj.Icon}>
                            {stateObj.label}
                        </Badge>
                    </span>
                );
            }
        }

        if (value === undefined && column.key.toString().includes("/")) {
            const keys = column.key.toString().split("/");
            const key1 = keys[0];
            const key2 = keys[1];

            const nestedObject = row[key1 as keyof T] as Record<string, any>;

            if (nestedObject != undefined) {
                value = nestedObject[key2];
            }
        }

        if (
            Array.isArray(column.key) &&
            column.key.every((k) => typeof k === "string")
        ) {
            const keys = column.key as string[];

            const mainKeys = keys.slice(0, 2); // 0 y 1
            const secondaryKey = keys[2]; // 2 (si existe)

            const mainText = mainKeys
                .map((k) => {
                    const v = resolveValueFromKey(row, k);
                    return v !== null && v !== undefined ? String(v) : "";
                })
                .filter(Boolean)
                .join(" - ");

            const secondaryText =
                secondaryKey !== undefined
                    ? (() => {
                          const v = resolveValueFromKey(row, secondaryKey);
                          return v !== null && v !== undefined ? String(v) : "";
                      })()
                    : "";

            // Si no hay nada, devuelve vacío
            if (!mainText && !secondaryText) return "";

            return (
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        lineHeight: 1.2,
                    }}
                >
                    <div
                        style={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                        }}
                    >
                        {mainText}
                    </div>

                    {secondaryText ? (
                        <div
                            style={{
                                marginTop: 2,
                                color: "rgba(0,0,0,0.55)", // sombreado/gris
                                fontSize: 12,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}
                            title={secondaryText} // tooltip al pasar
                        >
                            {secondaryText}
                        </div>
                    ) : null}
                </div>
            );
        }

        return value !== undefined &&
            value !== null &&
            (typeof value === "string" ||
                typeof value === "number" ||
                typeof value === "boolean")
            ? String(value)
            : value !== undefined && value !== null
              ? String(value)
              : "";
    };

    const resolveValueFromKey = (row: any, key: string) => {
        if (key.includes("/")) {
            return getNestedValue(row, key.split("/"));
        }
        return row?.[key];
    };

    const getTableClasses = () => {
        const classes = ["custom-table"];

        classes.push(`columns-${columns.length}`);

        if (enableRowSelection) {
            classes.push("has-checkboxes");
        }

        return classes.join(" ");
    };

    const getColumnClass = (isCheckbox = false, isActions = false) => {
        if (isCheckbox) return "checkbox-column";
        if (isActions) return "actions-column";
        return "content-column";
    };

    const mappedData = () => {
        const defaults: { [key: string]: any } =
            formElements === undefined
                ? {}
                : formElements
                      .flatMap((group) => group.elements)
                      .filter(
                          (el) =>
                              el.defaultValue !== undefined &&
                              el.visible === false
                      )
                      .reduce(
                          (acc, el) => {
                              acc[el.name] = el.defaultValue;
                              return acc;
                          },
                          {} as { [key: string]: any }
                      );

        const mappedData = Object.keys(modalData).reduce(
            (acc: { [key: string]: any }, key) => {
                const value = modalData[key];
                if (value && value.id !== undefined) {
                    acc[key] = value.id;
                } else {
                    acc[key] = value;
                }
                return acc;
            },
            {}
        );

        const merged = {
            ...defaults,
            ...mappedData,
        };

        const useForm = !filesAsBase64 && hasFileInData(merged);
        const payload = useForm ? mappedDataToFormData(merged) : merged;

        return payload;
    };

    const handleAddData = async () => {
        try {
            let formData = mappedData();
            if (filesAsBase64 && hasFileInData(formData)) {
                formData = await toBase64Payload(
                    formData as { [key: string]: any }
                );
            }
            if (payloadMapper) {
                formData = payloadMapper(
                    formData as { [key: string]: any }
                );
            }
            const response = await createItem(
                addApiUrl!,
                formData,
                keyProp,
                sortProp
            );

            let newItem: T | null = null;
            if (response && response.item) {
                newItem = response.item as T;
            } else {
                const newId = response?.newId ?? Date.now();
                newItem = { ...(formData as any), [keyProp]: newId } as T;
            }

            //   setFilteredData((prev) => {
            //     const next = sortByProperty([...prev, newItem as T], sortProp as keyof T);
            //     return next;
            //   });

            if (newItem) {
                const newData = [...(data ?? []), newItem];

                // 1) Notifica al padre con nueva lista
                onDataUpdate?.(newData);
                setModalData({});
                closeModal();
                setCurrentPage(1);
            }

            notificationRef.current?.addNotification(
                successMessages?.add ??
                    (t("ui.table.added", { name: elementName }) ?? ""),
                "success"
            );
        } catch (err) {
            console.error("Error al añadir:", err);
            notificationRef.current?.addNotification(
                (t("ui.table.addError", { name: elementName }) ?? ""),
                "error"
            );
        }
    };

    const handleEditDataAPI = async (row: T) => {
        try {
            const id = (row as Record<string, any>)[keyProp];
            let payload = mappedData();
            if (payloadMapper) {
                payload = payloadMapper(payload as { [key: string]: any });
            }
            const response = await updateItem(
                editApiUrl!,
                id,
                payload,
                keyProp,
                sortProp
            );

            // si el backend devuelve el item actualizado, úsalo; si no, combinamos
            let updatedItem: T | null = null;
            if (response && response.item) {
                updatedItem = response.item as T;
            } else {
                updatedItem = { ...(row as any), ...(payload as any) } as T;
            }

            const newData = (data ?? []).map((r: any) =>
                String(r[keyProp]) === String(id) ? updatedItem : r
            );

            // **Notifica al padre**
            onDataUpdate?.(newData);

            // setFilteredData((prev) =>
            //   prev.map((r) =>
            //     (r as any)[keyProp] === (updatedItem as any)[keyProp]
            //       ? (updatedItem as T)
            //       : r
            //   )
            // );

            notificationRef.current?.addNotification(
                successMessages?.edit ??
                    (t("ui.table.edited", { name: elementName }) ?? ""),
                "success"
            );

            closeModal();
        } catch (err) {
            console.error("Error al editar:", err);
            notificationRef.current?.addNotification(
                (t("ui.table.editError", { name: elementName }) ?? ""),
                "error"
            );
        }
    };

    const handleDeleteData = async (row: T) => {
        setRowToDelete(row);
        setConfirmationModalOpen(true);
    };

    const confirmDelete = async () => {
        if (!rowToDelete) return;
        try {
            const id = (rowToDelete as Record<string, any>)[keyProp];
            await deleteItem(deleteApiUrl!, id, keyProp, sortProp);

            const newSelection = new Set(selectedRows);

            newSelection.delete(id);

            const newData = (data ?? []).filter(
                (r: any) => String(r[keyProp]) !== String(id)
            );
            onDataUpdate?.(newData);

            onSelectionChange?.(newSelection);
            setConfirmationModalOpen(false);
            setRowToDelete(null);

            notificationRef.current?.addNotification(
                successMessages?.delete ??
                    (t("ui.table.deleted", { name: elementName }) ?? ""),
                "success"
            );
        } catch (err) {
            console.error("Error al eliminar:", err);
            notificationRef.current?.addNotification(
                (t("ui.table.deleteError", { name: elementName }) ?? ""),
                "error"
            );
            setConfirmationModalOpen(false);
            setRowToDelete(null);
        }
    };

    const calculateAssociatedOptions = (currentData: {
        [key: string]: any;
    }) => {
        const updatedOptions: { [key: string]: any[] } = {};

        formElements
            ?.flatMap((group) => group.elements)
            .forEach((element) => {
                if (
                    element.associatedFormElement &&
                    currentData[element.associatedFormElement]
                ) {
                    const parentId =
                        currentData[element.associatedFormElement]?.id ??
                        currentData[element.associatedFormElement];

                    if (!parentId) {
                        updatedOptions[element.name] = [];
                        return;
                    }

                    /* Comparación por String: el padre puede llegar como número
                       (campo tal cual del dato al editar) o como string (valor
                       de un <select>), y fatherId puede venir mapeado en
                       cualquiera de los dos tipos según el origen de las
                       opciones. Comparar con === los deja desincronizados y
                       vacía las opciones del hijo (p. ej. tipo de residuo). */
                    const parentIdStr = String(parentId);

                    updatedOptions[element.name] =
                        element.options?.filter((option) => {
                            if (Array.isArray(option.fatherId)) {
                                return option.fatherId.some(
                                    (id) => String(id) === parentIdStr
                                );
                            }
                            return String(option.fatherId) === parentIdStr;
                        }) || [];
                }
            });

        return updatedOptions;
    };

    const handleEditData = (row: T) => {
        /* Si el formulario trabaja en otra unidad que el dato, se convierte aquí;
           payloadMapper hace la conversión inversa al guardar. */
        const initialData = (
            rowToFormMapper ? rowToFormMapper(row) : { ...row }
        ) as T;

        const initialAssociatedOptions =
            calculateAssociatedOptions(initialData);
        setAssociatedOptions(initialAssociatedOptions);

        setModalType("edit");
        setModalData(initialData);
        setIsModalOpen(true);
    };

    const handleExportData = (row: T) => {
        exportObraReport({ row });
    };

    const handleInputChangeModal = (name: string, value: any) => {
        const normalized = normalizeDropdownValue(value);

        const tipo = (modalType === "edit" && editFormElements
            ? editFormElements
            : formElements)!
        .flatMap(section => section.elements)
        .find(element => element.name === name)
        ?.type;

        var finalValue = normalized;

        // 👇 si quieres que multiselect nunca sea null, conviértelo aquí
        if(tipo == "multiselect" && normalized === null) {
            finalValue = normalized === null ? [] : normalized;
        }

        setModalData((prev) => {
            const newModalData = { ...prev, [name]: finalValue };
            setAssociatedOptions(calculateAssociatedOptions(newModalData));
            return newModalData;
        });
    };

    const normalizeDropdownValue = (value: any) => {
        if (value == null) return null;

        // Multi
        if (Array.isArray(value)) {
            return value.map((v) =>
                v && typeof v === "object" && "value" in v ? v.value : v
            );
        }

        // Single
        if (typeof value === "object" && "value" in value) {
            return value.value;
        }

        return value;
    };

    const isEmptyValue = (elementType: string, value: any): boolean => {
        // null o undefined siempre vacío
        if (value === null || value === undefined) return true;

        switch (elementType) {
            case "text":
            case "email":
            case "password":
            case "tel":
            case "textarea":
                return String(value).trim() === "";

            case "number":
                // acepta 0 como valor válido si es requerido
                return String(value).trim() === "" || isNaN(Number(value));

            case "checkbox":
                // si es requerido, debe ser true
                return value !== true;

            case "radio":
                // debe existir un valor seleccionado no vacío
                return String(value).trim() === "";

            case "select":
                // soporta tanto primitivo como objeto {id|value}
                if (Array.isArray(value)) return value.length === 0; // por si 'withCheckbox' habilita múltiples
                if (typeof value === "object") {
                    return value.value === undefined && value.id === undefined;
                }
                return String(value).trim() === "";

            case "multiselect":
                // array con al menos un valor
                if (!Array.isArray(value)) return true;
                return value.length === 0;

            case "document":
                // depende de cómo lo guardes: base64, File, FormData...
                // criterio común: debe existir algún contenido no vacío
                if (typeof value === "string") return value.trim() === ""; // base64
                if (value && typeof value === "object") {
                    // File o similar
                    if ("size" in value) return (value.size as number) <= 0;
                    // objeto con campos: intenta detectar 'file'/'base64'
                    if ("file" in value) return !value.file;
                    if ("base64" in value) return !value.base64;
                }
                return !value;

            case "date":
                // cadena de fecha válida
                const str = String(value).trim();
                if (str === "") return true;
                const d = new Date(str);
                return isNaN(d.getTime());

            default:
                // por defecto: cadena vacía
                return String(value).trim() === "";
        }
    };

    const getMissingRequiredFields = (): { name: string; label?: string }[] => {
        const effectiveForm =
            modalType === "edit" && editFormElements
                ? editFormElements
                : formElements ?? [];

        const isElementVisible = (el: any): boolean => {
            if (!el.visible) return false;
            if (el.visibleWhen) {
                const conditions = Array.isArray(el.visibleWhen)
                    ? el.visibleWhen
                    : [el.visibleWhen];
                return conditions.every(({ field, value: expectedValue }: any) => {
                    const fieldValue = modalData[field];
                    if (Array.isArray(fieldValue)) {
                        return fieldValue.includes(expectedValue);
                    }
                    return (
                        String(fieldValue ?? "") === String(expectedValue ?? "")
                    );
                });
            }
            return true;
        };

        const groups = (effectiveForm ?? []).filter((g) =>
            g.elements?.some((e) => isElementVisible(e))
        );

        const missing: { name: string; label?: string }[] = [];

        groups.forEach((group) => {
            group.elements
                .filter(
                    (el) =>
                        isElementVisible(el) &&
                        el.required &&
                        !el.disabled
                )
                .forEach((el) => {
                    const val = modalData[el.name];
                    if (isEmptyValue(el.type, val)) {
                        missing.push({ name: el.name, label: el.label });
                    }
                });
        });

        return missing;
    };

    const validateRequiredFields = (): boolean => {
        const missing = getMissingRequiredFields();
        if (missing.length > 0) {
            const names = missing.map((m) => m.name);
            setMissingNames(names);
            const fieldsList = missing.map((m) => m.label ?? m.name).join(", ");
            notificationRef.current?.addNotification(
                (t("ui.table.requiredFields", { fields: fieldsList }) ?? ""),
                "info"
            );
            return false;
        }
        setMissingNames([]);
        return true;
    };

    const exportObraReport = async ({ row }: { row: T }) => {
        const api = getApiInstance();

        const CodObra = (row as any)[keyProp];
        const CodZona = selectedZone?.value || 0;
        const CodZonaLegal = selectedZone?.value || 0;
        const extraProp = (row as any)[extraPropExportKey] || null;
        const extraPropText = extraPropExportText;

        console.log(row);

        console.log("Iniciando exportación con:", {
            keyProp: CodObra,
            CodZona,
            CodZonaLegal,
            extraProp: extraProp ?? "N/A",
        });

        try {
            const response = await api.post(
                exportApiUrl,
                null, // cuerpo vacío; los parámetros van en query string
                {
                    params: {
                        [keyProp]: CodObra,
                        CodZona,
                        CodZonaLegal,
                        ...(extraProp != null && {
                            [extraPropText]: extraProp,
                        }),
                    },
                    responseType: "blob",
                }
            );

            // Si por alguna razón el servidor devolviera JSON, intenta interpretarlo.
            const contentType = (
                response.headers["content-type"] || ""
            ).toLowerCase();
            if (contentType.includes("application/json")) {
                const text = await response.data.text();
                let json;
                try {
                    json = JSON.parse(text);
                } catch {
                    json = { message: text };
                }
                throw new Error(
                    json?.message ||
                        (t("ui.table.serverJson") ?? "")
                );
            }

            // Nombre de archivo desde Content-Disposition (soporta filename y filename*)
            const cd = response.headers["content-disposition"] || "";
            let fileName = `Informe_${CodObra}.xlsx`;

            const utf8Match = cd.match(/filename\*=UTF-8''([^;]+)/i);

            const normalMatch = cd.match(/filename="?([^"]+)"?/i);

            const rawFileName = utf8Match?.[1] || normalMatch?.[1];

            if (rawFileName) {
                try {
                    fileName = decodeURIComponent(rawFileName.trim());
                } catch {
                    fileName = rawFileName.trim();
                }
            }
            // Blob
            const blob =
                response.data instanceof Blob
                    ? response.data
                    : new Blob([response.data], {
                          type:
                              response.headers["content-type"] ||
                              "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                      });

            const urlBlob = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = urlBlob;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(urlBlob);

            return { ok: true, fileName, size: blob.size };
        } catch (err) {
            // Si tienes un handler global (setError), úsalo; aquí dejamos retorno estructurado
            console.error(err);
            return { ok: false, error: err };
        }
    };    

    const showTopContent =
        Boolean(topLeftContent) ||
        Boolean(extraTopButtons) ||
        (canChangeState && effectiveSelectedRows.size > 0) ||
        (!hideAddButton &&
            ((formElements != undefined && formElements?.length > 0) ||
                (editFormElements != undefined &&
                    editFormElements?.length > 0)) &&
            userCodAcceso === 1 &&
            puedeAnadir);

    return (
        <div className="table-card">
            {showTopContent && (
            <div className="table-card__top-content">
                {topLeftContent}
                <div
                    style={{
                        display: "flex",
                        gap: "12px",
                        alignItems: "center",
                        marginLeft: "auto",
                    }}
                >
                    {canChangeState && effectiveSelectedRows.size > 0 && (
                        <Button
                            variant="filled"
                            onClick={openBulkStateModal}
                            endIcon={undefined}
                            startIcon={MdOutlineEdit({})}
                        >
                            Cambiar estado ({effectiveSelectedRows.size})
                        </Button>
                    )}

                    {!hideAddButton &&
                        ((formElements != undefined && formElements?.length > 0) ||
                            (editFormElements != undefined &&
                                editFormElements?.length > 0)) &&
                        userCodAcceso === 1 &&
                        puedeAnadir && (
                            <Button
                                variant="filled"
                                startIcon={IoMdAdd({})}
                                onClick={openModal}
                                endIcon={undefined}
                            >
                                {`${(t("common.add") ?? "")} ${elementName}`}
                            </Button>
                        )}

                    {extraTopButtons}
                </div>
            </div>
            )}

            {!hideFilterBar && (
            <div className="table-card__filter-row">
                <FilterBar
                    rows={filteredData}
                    state={filterState}
                    onChange={(next: any) => {
                        setFilterState(next);
                        setCurrentPage(1);
                    }}
                    fields={filterBarFields}
                    excludeFields={excludeFields}
                    resultCount={filteredRows.length}
                    totalCount={filteredData.length}
                    showAdvanced={showAdvancedFilters}
                />
            </div>
            )}

            <div className="table-card__content">
                <div className="table-container">
                    <table className={getTableClasses()}>
                        <thead>
                            <tr>
                                {expandable && isMobile && (
                                    <th className="expand-column"></th>
                                )}
                                {enableRowSelection && (
                                    <th className={getColumnClass(true, false)}>
                                        <div
                                            className={`table-checkbox ${
                                                selectAll ? "checked" : ""
                                            }`}
                                            onClick={handleSelectAll}
                                        />
                                    </th>
                                )}
                                {columns
                                    .filter(
                                        (x) =>
                                            x.visible &&
                                            !(isMobile && x.visibleOnExpand)
                                    )
                                    .map((col) => (
                                        <th
                                            key={String(col.key)}
                                            className={`${getColumnClass(
                                                false,
                                                false
                                            )} ${
                                                col.sortable !== false
                                                    ? "sortable"
                                                    : ""
                                            }`}
                                            onClick={() =>
                                                col.sortable !== false &&
                                                handleColumnSort(col.key)
                                            }
                                        >
                                            {col.label}
                                            {sortConfig.key === col.key
                                                ? sortConfig.direction ===
                                                  "ascending"
                                                    ? " ▲"
                                                    : " ▼"
                                                : null}
                                        </th>
                                    ))}
                                {hayColumnaAcciones && (
                                        <th
                                            className={getColumnClass(
                                                false,
                                                true
                                            )}
                                        >
                                            {(t("common.actions") ?? "")}
                                        </th>
                                    )}
                            </tr>
                        </thead>
                        <tbody>
                            {loadingData ? (
                                Array.from({ length: rowsPerPage }).map(
                                    (_, rowIdx) => (
                                        <tr key={`skeleton-${rowIdx}`}>
                                            {expandable && isMobile && (
                                                <td className="expand-column skeleton-cell"></td>
                                            )}
                                            {enableRowSelection && (
                                                <td className="checkbox-column skeleton-cell"></td>
                                            )}
                                            {columns
                                                .filter((c) =>
                                                    isMobile
                                                        ? c.visible &&
                                                          !c.visibleOnExpand
                                                        : c.visible
                                                )
                                                .map((col, colIdx) => (
                                                    <td
                                                        key={`skeleton-${rowIdx}-${colIdx}`}
                                                        className={`content-column skeleton-cell`}
                                                    ></td>
                                                ))}
                                            {hayColumnaAcciones && (
                                                    <td className="actions-column skeleton-cell"></td>
                                                )}
                                        </tr>
                                    )
                                )
                            ) : currentRows.length > 0 ? (
                                currentRows.map((row, index) => {
                                    const rowId =
                                        (row[keyProp as keyof T] as number) ||
                                        index;
                                    const isExpanded = expandedRows.has(rowId);

                                    return (
                                        <React.Fragment key={rowId}>
                                            <tr
                                                className={`${
                                                    effectiveSelectedRows.has(
                                                        rowId
                                                    )
                                                        ? "selected"
                                                        : ""
                                                } ${
                                                    isExpanded ? "expanded" : ""
                                                } ${
                                                    getRowClassName
                                                        ? getRowClassName(row)
                                                        : ""
                                                }`}
                                                onClick={() => {
                                                    if (onRowClick) {
                                                        onRowClick(row);
                                                    } else if (
                                                        editOnRowClick
                                                    ) {
                                                        handleEditData(row);
                                                    }
                                                }}
                                                style={{
                                                    cursor:
                                                        onRowClick ||
                                                        editOnRowClick
                                                            ? "pointer"
                                                            : undefined,
                                                }}
                                            >
                                                {expandable && isMobile && (
                                                    <td className="expand-column">
                                                        <div
                                                            className="expand-icon"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                toggleRowExpansion(
                                                                    rowId
                                                                );
                                                            }}
                                                        >
                                                            {isExpanded
                                                                ? MdKeyboardArrowUp(
                                                                      {
                                                                          size: 20,
                                                                      }
                                                                  )
                                                                : MdKeyboardArrowDown(
                                                                      {
                                                                          size: 20,
                                                                      }
                                                                  )}
                                                        </div>
                                                    </td>
                                                )}
                                                {enableRowSelection && (
                                                    <td
                                                        className={getColumnClass(
                                                            true,
                                                            false
                                                        )}
                                                    >
                                                        <div
                                                            className={`table-checkbox ${
                                                                effectiveSelectedRows.has(
                                                                    rowId
                                                                )
                                                                    ? "checked"
                                                                    : ""
                                                            }`}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleSelectRow(
                                                                    rowId
                                                                );
                                                            }}
                                                        />
                                                    </td>
                                                )}
                                                {columns
                                                    .filter(
                                                        (x) =>
                                                            x.visible &&
                                                            !(
                                                                isMobile &&
                                                                x.visibleOnExpand
                                                            )
                                                    )
                                                    .map((col) => (
                                                        <td
                                                            key={`${rowId}-${String(
                                                                col.key
                                                            )}`}
                                                            title={String(
                                                                getNestedValue(
                                                                    row,
                                                                    col.key
                                                                )
                                                            )}
                                                            className={`${getColumnClass(
                                                                false,
                                                                false
                                                            )} ${
                                                                col.align
                                                                    ? `text-${col.align}`
                                                                    : ""
                                                            }`}
                                                            style={{
                                                                ...(col.width && {
                                                                    width: col.width,
                                                                    minWidth:
                                                                        col.width,
                                                                    maxWidth:
                                                                        col.width,
                                                                }),
                                                            }}
                                                        >
                                                            {renderCellValue(
                                                                row,
                                                                col
                                                            )}
                                                        </td>
                                                    ))}
                                                {hayColumnaAcciones && (
                                                        <td
                                                            className={getColumnClass(
                                                                false,
                                                                true
                                                            )}
                                                        >
                                                            <OptionsMenu
                                                                row={row}
                                                                options={
                                                                    accionesDeFila(
                                                                        row
                                                                    ).map(
                                                                        (
                                                                            action
                                                                        ) => {
                                                                            if (
                                                                                action ===
                                                                                "Eliminar"
                                                                            ) {
                                                                                return {
                                                                                    icon: MdDelete(
                                                                                        {}
                                                                                    ),
                                                                                    label: (t(
                                                                                        "common.delete"
                                                                                    ) ?? ""),
                                                                                    danger: true,
                                                                                    separator: true,
                                                                                    action: () =>
                                                                                        handleDeleteData(
                                                                                            row
                                                                                        ),
                                                                                };
                                                                            } else if (
                                                                                action ===
                                                                                "Editar"
                                                                            ) {
                                                                                return {
                                                                                    icon: MdEdit(
                                                                                        {}
                                                                                    ),
                                                                                    label: (t(
                                                                                        "common.edit"
                                                                                    ) ?? ""),
                                                                                    action: () =>
                                                                                        handleEditData(
                                                                                            row
                                                                                        ),
                                                                                };
                                                                            } else if (
                                                                                action ===
                                                                                "Exportar"
                                                                            ) {
                                                                                return {
                                                                                    icon: MdOutlineFileDownload(
                                                                                        {}
                                                                                    ),
                                                                                    label:
                                                                                        exportActionLabel ??
                                                                                        (t(
                                                                                            "common.export"
                                                                                        ) ?? ""),
                                                                                    action: () =>
                                                                                        handleExportData(
                                                                                            row
                                                                                        ),
                                                                                };
                                                                            } else if (
                                                                                typeof action ===
                                                                                "object"
                                                                            ) {
                                                                                // ✅ Acción custom con render propio
                                                                                if (
                                                                                    action.render
                                                                                ) {
                                                                                    return {
                                                                                        label: action.label,
                                                                                        icon: action.icon,
                                                                                        render: () =>
                                                                                            action.render!(
                                                                                                row
                                                                                            ),
                                                                                    };
                                                                                }
                                                                                // ✅ Acción de validación (abre el modal de estado)
                                                                                if (
                                                                                    action.actionType ===
                                                                                    "validate"
                                                                                ) {
                                                                                    return {
                                                                                        label: action.label,
                                                                                        icon: action.icon,
                                                                                        action: () =>
                                                                                            openStateModal(
                                                                                                row
                                                                                            ),
                                                                                    };
                                                                                }
                                                                                // ✅ Acción custom con handler
                                                                                return {
                                                                                    label: action.label,
                                                                                    icon: action.icon,
                                                                                    danger: action.danger,
                                                                                    separator:
                                                                                        action.separator,
                                                                                    action: action.action
                                                                                        ? () =>
                                                                                              action.action!(
                                                                                                  row
                                                                                              )
                                                                                        : () => {},
                                                                                };
                                                                            } else {
                                                                                return {
                                                                                    label: action as string,
                                                                                    action: () =>
                                                                                        console.log(
                                                                                            `${action} action clicked`
                                                                                        ),
                                                                                };
                                                                            }
                                                                        }
                                                                    )
                                                                }
                                                            />
                                                        </td>
                                                    )}
                                            </tr>
                                            {expandable &&
                                                isMobile &&
                                                isExpanded && (
                                                    <tr className="expanded-row">
                                                        <td
                                                            colSpan={
                                                                columns.filter(
                                                                    (c) =>
                                                                        c.visible &&
                                                                        !c.visibleOnExpand
                                                                ).length +
                                                                (enableRowSelection
                                                                    ? 1
                                                                    : 0) +
                                                                (expandable &&
                                                                isMobile
                                                                    ? 1
                                                                    : 0) +
                                                                (hayColumnaAcciones
                                                                    ? 1
                                                                    : 0)
                                                            }
                                                            className="expanded-content"
                                                        >
                                                            {renderExpandedContent ? (
                                                                renderExpandedContent(
                                                                    row
                                                                )
                                                            ) : (
                                                                <div className="expanded-content__grid">
                                                                    {columns
                                                                        .filter(
                                                                            (
                                                                                col
                                                                            ) =>
                                                                                col.visibleOnExpand
                                                                        )
                                                                        .map(
                                                                            (
                                                                                col
                                                                            ) => (
                                                                                <div
                                                                                    key={String(
                                                                                        col.key
                                                                                    )}
                                                                                    className="expanded-content__item"
                                                                                >
                                                                                    <span className="expanded-content__label">
                                                                                        {
                                                                                            col.label
                                                                                        }

                                                                                        :
                                                                                    </span>
                                                                                    <span className="expanded-content__value">
                                                                                        {renderCellValue(
                                                                                            row,
                                                                                            col
                                                                                        )}
                                                                                    </span>
                                                                                </div>
                                                                            )
                                                                        )}
                                                                </div>
                                                            )}
                                                        </td>
                                                    </tr>
                                                )}
                                        </React.Fragment>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td
                                        colSpan={
                                            columns.filter(
                                                (c) =>
                                                    c.visible &&
                                                    !(
                                                        isMobile &&
                                                        c.visibleOnExpand
                                                    )
                                            ).length +
                                            (actions ? 1 : 0) +
                                            (enableRowSelection ? 1 : 0) +
                                            (expandable && isMobile ? 1 : 0)
                                        }
                                        className="no-results-cell"
                                    >
                                        <div className="no-results-cell__content">
                                            {MdSearchOff({
                                                className:
                                                    "no-results-cell__content__icon",
                                            })}
                                            <p className="no-results-cell__content__text">
                                                {noResultsMessage ||
                                                    (t("ui.table.noResults") ?? "")}
                                            </p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {totalPages > 1 && (
                    <div className="table-card__pagination">
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            itemsPerPage={rowsPerPage}
                            totalItems={filteredRows.length}
                            onPageChange={setCurrentPage}
                        />
                    </div>
                )}
                <Modal
                    isOpen={isModalOpen}
                    onClose={closeModal}
                    position="side"
                    actions={
                        <>
                            <Button
                                variant="outlined"
                                onClick={closeModal}
                                endIcon={undefined}
                                startIcon={undefined}
                            >
                                {(t("common.cancel") ?? "")}
                            </Button>
                            <Button
                                variant="filled"
                                onClick={() => {
                                    // 1) validar requeridos
                                    const ok = validateRequiredFields();
                                    if (!ok) return;

                                    // 1.5) validación propia de la página, si la hay
                                    if (validateForm) {
                                        const customError = validateForm(
                                            modalData,
                                            modalType === "edit"
                                                ? "edit"
                                                : "add"
                                        );
                                        if (customError) {
                                            notificationRef.current?.addNotification(
                                                customError,
                                                "info"
                                            );
                                            return;
                                        }
                                    }

                                    // 2) continuar con la acción correspondiente
                                    if (modalType === "edit") {
                                        handleEditDataAPI(modalData as T);
                                    } else {
                                        handleAddData();
                                    }
                                }}
                                endIcon={undefined}
                                startIcon={undefined}
                            >
                                {(t("common.save") ?? "")}
                            </Button>
                        </>
                    }
                    title={
                        modalType === "edit"
                            ? `${(t("common.edit") ?? "")} ${elementName}`
                            : modalAddTitle ||
                              `${(t("common.add") ?? "")} ${elementName}`
                    }
                >
                    <FormModal
                        dynamicFormElements={dynamicFormElements}
                        modalData={modalData}
                        associatedOptions={associatedOptions}
                        handleInputChange={handleInputChangeModal}
                        missingNames={missingNames}
                    />
                </Modal>

                {confirmationModalOpen && (
                    <Modal
                        isOpen={confirmationModalOpen}
                        onClose={() => setConfirmationModalOpen(false)}
                        position="center"
                        actions={
                            <>
                                <Button
                                    variant="outlined"
                                    onClick={() =>
                                        setConfirmationModalOpen(false)
                                    }
                                    endIcon={undefined}
                                    startIcon={undefined}
                                >
                                    {(t("common.cancel") ?? "")}
                                </Button>
                                <Button
                                    variant="filled"
                                    onClick={confirmDelete}
                                    endIcon={undefined}
                                    startIcon={undefined}
                                >
                                    {(t("common.confirm") ?? "")}
                                </Button>
                            </>
                        }
                        title={(t("ui.table.confirmDeleteTitle") ?? "")}
                    >
                        <p className="modal-description">
                            {(t("ui.table.confirmDeleteMessage") ?? "")}
                        </p>
                    </Modal>
                )}
            </div>

            {isStateModalOpen && (
                <Modal
                    isOpen={isStateModalOpen}
                    onClose={closeStateModal}
                    position="center"
                    actions={
                        <div
                            className="modal-actions"
                            style={{ marginTop: 16 }}
                        >
                            <Button
                                variant="filled"
                                onClick={confirmStateChange}
                                startIcon={undefined}
                                endIcon={undefined}
                            >
                                {isChangingState
                                    ? (t("ui.table.updating") ?? "")
                                    : (t("ui.table.accept") ?? "")}
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={closeStateModal}
                                startIcon={undefined}
                                endIcon={undefined}
                            >
                                {(t("common.cancel") ?? "")}
                            </Button>
                        </div>
                    }
                    title={(t("ui.table.changeState") ?? "")}
                >
                    <InputDropdown_New
                        label={(t("ui.table.newState") ?? "")}
                        options={stateChangeOptions || []}
                        defaultValue={newStateValue}
                        onChange={(value: any) => setNewStateValue(value)}
                        disabled={isChangingState}
                    />
                    <InputTextarea
                        label={(t("fields.observations") ?? "")}
                        value={observations}
                        onChange={(e) => setObservations(e.target.value)}
                        rows={6}
                    />
                </Modal>
            )}

            <Notification ref={notificationRef} />
        </div>
    );
});

export default Table;
